import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
  LEGACY_SCREEN_VIEW,
} from "../../common_nextgen/utils/Constants";

import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";

import { formFields } from "./AlternateCopayOverrideConfig";
import { useModal } from "../../common_nextgen/hooks/ModalContext";
import { LegacyResolvers } from "../../common_nextgen/utils/UtilFunctions";

const screenInfo = {
  ADD: ["Add Alternate Copay PSC Override", "RCACP003"],
  CHANGE: ["Alternate Copay PSC Override Detail", "RCACP002"],
  VIEW: ["Alternate Copay PSC Override Detail", "RCACP002"],
};

const AlternateCopayPscOverrideForm = ({ mode }) => {
  const location = useLocation();
  const { ALTERNATE_COPPAY_OVERRIDE } = ROUTE_PATHS;

  //const { state: stateVal } = location;
  const stateVal = location.state || [];
  let [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);

  let {
    selectedPriceSchedules = [],
    previousScreen = ALTERNATE_COPPAY_OVERRIDE,
    currentIndex: initialIndex = 0,
    paramsData,
  } = currentState;
  // const { selectedPriceSchedules = [], previousScreen = '/alternate-copay-override', currentIndex: initialIndex = 0 } = location.state || {};

  // const { bscScheduleName, bscDescription, bsdStepNumber, bmdScheduleCode, acpProdSelCode, bmdDescription, acpMultiSourceCode } = location.state || {};
  const navigateToPage = useCustomNavigate();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();
  const { axios_helper_no_header } = useAxios();
  const { showModal } = useModal();
  const [lookupData, setLookupData] = useState({});
  const handleReceiveData = (data) => {
    //setInitialFormData({ ...initialFormData, ...data });
    if (data && Object.keys(data).length) {
      setLookupData(data);
    }
  };
  const fetchLookupData = (lookupScreen, params, formData) => {
    showModal(lookupScreen, { params }, handleReceiveData, formData);
  };

  const viewLookupDetails = (lookupScreen, params, formData) => {
    const { viewType, fieldMapping } = params;
    const { default: screenNumber } = lookupScreen;
    if (viewType === "legacy") {
      const { default: viewParams } = fieldMapping;
      const paramData = viewParams.reduce((acc, item) => {
        const resolver = item["resolver"]
          ? LegacyResolvers[item["resolver"]]
          : null;
        acc[item["legacy"]] = resolver
          ? resolver(formData[item["source"]])
          : formData[item["source"]];
        return acc;
      }, {});
      const message = {
        message: "navigate-to-presto",
        programCode: LEGACY_SCREEN_VIEW[screenNumber],
        parameters: paramData,
      };
      console.log("message", message);
      window.top?.postMessage(message, "*");
    } else {
      showModal(lookupScreen, { params }, null, formData, true);
    }
  };

  const handleBack = () => {
    if (currentIndex < selectedPriceSchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = useCallback(() => {
    const [screenTitle, screenNumber] = isAddMode(currentMode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  }, [currentMode]);

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/altCopayPscOverride";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/altCopayPscOverride";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];
    }

    if (dataForSubmission === initialFormData) {
      toast.current.show({
        severity: "info",
        summary: "",
        detail: "No Change of Information",
        life: 3000,
      });
      return;
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      setInitialFormData({
        ...responseData,
        bmdDescription: paramsData.bmdDescription,
      });
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        currentState = {
          ...currentState,
          selectedPriceSchedules: [
            { ...responseData, bmdDescription: paramsData.bmdDescription },
          ],
          currentIndex: 0,
        };
        navigateToPage(ROUTE_PATHS.ALTERNATE_COPAY_OVERRIDE_EDIT, [
          ...previousState,
          currentState,
        ]);
      } else {
        setInitialFormData({
          ...responseData,
          bmdDescription: paramsData.bmdDescription,
        });
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.F12 = "Back";
      keyName.chmod = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.F12 = "Back";
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, initialFormData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const params = isAddMode(currentMode)
        ? {
            bscScheduleName: paramsData.bscScheduleName,
            bsdStepNumber: paramsData.bsdStepNumber,
          }
        : {
            bscScheduleName: paramsData.bscScheduleName,
            bsdStepNumber: paramsData.bsdStepNumber,
            bmdScheduleCode: paramsData.bmdScheduleCode,
            acpProdSelCode: selectedPriceSchedules[currentIndex].acpProdSelCode,
            acpMultiSourceCode:
              selectedPriceSchedules[currentIndex].acpMultiSourceCode,
          };
      const url = isAddMode(currentMode)
        ? "api/altCopayPscOverride/initialFormData/RCACP003"
        : `api/altCopayPscOverride/${encodeURIComponent(
            selectedPriceSchedules[currentIndex]
          )}`;
      const { responseData } = await axios_helper_no_header(
        "GET",
        url,
        {},
        params
      );
      setInitialFormData({
        ...responseData,
        bmdDescription: paramsData.bmdDescription,
      });
    };

    fetchInitialData();
  }, [mode, currentIndex]);

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          lookupData={lookupData}
          fetchLookupData={fetchLookupData}
          viewLookupDetails={viewLookupDetails}
        />
      </div>
    </div>
  );
};

export default AlternateCopayPscOverrideForm;
